import stampit from 'stampit'
import Utterance from './utterance'
import Voice from './voice'
import Play from './play'
import EventEmitter from 'events'
// Here's a stamp with public methods, and some state:
const Clip = stampit({
  props: {
    utterances: [],
    voice: {},
    faceId: 'INVALID-FACE-ID'
  },
  statics: {
    randomPlayId: 0,
    ssUtterances: [],
    ssUtteranceIndex: 0,
    eventEmitter: new EventEmitter(),
    stop: function () {
      Clip.ssUtterances.forEach((play) => {
        play.stop()
      })
      Clip.ssUtterances = []
      Clip.randomPlayId = 0
    },
    isPlaying: function () {
      if (Clip.ssUtterances.length > Clip.ssUtteranceIndex) {
        return Clip.ssUtterances[Clip.ssUtteranceIndex].isPlaying()
      }
    }
  },
  init ({utterances, faceId, voice}) {
    if (voice) {
      this.voice = voice
    } else {
      this.voice = Voice({locale: 'en-US', bIsMale: false})
    }

    this.faceId = faceId || this.faceId
    this.utterances = utterances || this.utterances
  },
  methods: {
    play (onFinishedClip, onStartClip, maxDelay) {
      Clip.randomPlayId = Math.random()
      var localPlayId = Clip.randomPlayId
      Clip.ssUtterances = []
      this.utterances.forEach((ut) => {
        var playData = {
          utterance: ut,
          voice: this.voice,
          maxDelay: maxDelay
        }
        Clip.ssUtterances.push(Play(playData))
      })
      // console.log(Clip.ssUtterances)
      // Now setup the events
      if (Clip.ssUtterances.length > 0) {
        Clip.ssUtterances[0].eventEmitter.addListener('start', () => {
          if (onStartClip) {
            onStartClip()
          }
        })
        Clip.ssUtterances[Clip.ssUtterances.length - 1].eventEmitter.addListener('end', () => {
          if (onFinishedClip) {
            onFinishedClip()
          }
          // Calling this causes scenes to stop playing randomly.
          // I'm guessing that 'end' is called when we pause with  playAfterDelay
          // Clip.ssUtterances = []
        })
        for (let i = 0, l = Clip.ssUtterances.length; i < l; ++i) {
          Clip.ssUtterances[i].eventEmitter.addListener('start', (dur) => {
            Clip.eventEmitter.emit('utterancestart', {'duration': dur, 'faceId': this.faceId})
            if (i + 1 === l) {
              setTimeout(() => {
                if (localPlayId === Clip.randomPlayId) {
                  Clip.eventEmitter.emit('lastUttereranceEndPrediction')
                }
              }, dur)
            }
          })
          Clip.ssUtterances[i].eventEmitter.addListener('stalled', (dur) => {
            Clip.eventEmitter.emit('stalled')
          })
        }
        for (let i = 0, l = Clip.ssUtterances.length - 1; i < l; ++i) {
          ((play1, play2, index) => {
            play1.eventEmitter.addListener('end', () => {
              // If the user plays something else (or this scene again)
              // the localPlayID won't match the static variable
              if (localPlayId === Clip.randomPlayId) {
                Clip.ssUtteranceIndex = index
                play2.play()
              }
            })
          })(Clip.ssUtterances[i], Clip.ssUtterances[i + 1], i + 1)
        }
        Clip.ssUtterances[0].play()
        Clip.ssUtteranceIndex = 0
      }
    },
    add (utterance) {
      this.utterances.push(utterance)
      return this
    },
    text () {
      let combinedText = ''
      let numUtterances = this.utterances.length
      for (let i = 0; i < numUtterances; ++i) {
        combinedText = combinedText + this.utterances[i].text + ' '
      }
      return combinedText.trim()
    },

    updateText (text) {
      let newUtterances = this._splitUtterances(text)
      let curLen = this.utterances.length
      let newLen = newUtterances.length
      // Easy case, the number of Utterances didn't change
      if (curLen === newLen) {
        for (let i = 0; i < curLen; ++i) {
          this.utterances[i].text = newUtterances[i].text
        }
        return
      }
      // Store the original utterances in a dictionary with text as key.
      let utteranceDict = {}
      for (let i = 0; i < curLen; ++i) {
        if (this.utterances[i].text in utteranceDict) {
          // handle duplicate utterances with different pitch/rate
          utteranceDict[this.utterances[i].text].push(this.utterances[i])
        } else {
          utteranceDict[this.utterances[i].text] = [this.utterances[i]]
        }
      }

      let defaultRateIndices = []
      for (let i = 0; i < newLen; ++i) {
        let newText = newUtterances[i].text
        if (newText in utteranceDict) {
          newUtterances[i] = utteranceDict[newText].shift()
          if (utteranceDict[newText].length === 0) {
            delete utteranceDict[newText]
          }
        } else {
          defaultRateIndices.push(i)
        }
      }

      // Match the leftovers
      if (defaultRateIndices.length > 0 && Object.values(utteranceDict).length > 0) {
        let cachedText = newUtterances[defaultRateIndices[0]].text
        newUtterances[defaultRateIndices[0]] = Object.values(utteranceDict)[0][0]
        newUtterances[defaultRateIndices[0]].text = cachedText
      }
      this.utterances = newUtterances
    },
    /**
   * Convert a string containing punctuation-separated utterances into an array
   * of utterances
   * @param {string} text - The string containing punctuation-separated utterances.
   * @return {Utterance[]} An array of Utterance objects
   */
    _splitUtterances (text) {
      // Splitting each utterance up using punctuation is important.  Intra-utterance
      // punctuation will add silence to the tts which looks bad unless the mouth stops moving
      // correctly. Better to split it into separate utterances so play_for_duration will move when
      // talking, and be on frame 0 when not.
      let utterances = []

      // split everything betwen deliminators [.?,!], but include the deliminator.
      let substrings = text.match(/[^.?,!]+[.?,!]?/g)

      if (substrings) {
        for (let i = 0, l = substrings.length; i < l; ++i) {
          let str = substrings[i].trim()

          // Make sure there is something to say other than the deliminator
          let numpunc = (str.match(/[.?,!]/g) || []).length

          if (str.length - numpunc > 0) {
            utterances.push(Utterance({text: str}))
          }
        }
      }
      return utterances
    }
  }
})
export default Clip



// WEBPACK FOOTER //
// ./src/tts/clip.js